import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect } from "react";
import {
  useAddPaymentMutation,
  useUpdatePaymentMutation,
} from "src/api/settings/payments";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  balance: yup.number().required("Required"),
  // is_cash: yup.boolean().required("Required"),
  type: yup
    .string()
    .required("")
    .oneOf(["is_cash", "is_credit"] as const),
  is_discount: yup.boolean().required("Required"),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IModel {
  open: boolean;
  handleClose: () => void;
  paymentData?: any;
  refetch: Function;
  isCashOnly?: boolean; // when it comes from tree
}

const AddEditPayment = (props: IModel) => {
  const { open, handleClose, paymentData, refetch, isCashOnly } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !paymentData ? "add" : "edit";
  const { control, handleSubmit, formState, reset } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      type: "is_cash",
      is_discount: false,
    },
  });
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  // APIS
  const { mutate, isPending, status, error } = useAddPaymentMutation();
  const {
    mutate: mutateUpdate,
    isPending: isPendingUpdate,
    status: statusUpdate,
    error: errorUpdate,
  } = useUpdatePaymentMutation();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { is_discount, type, name, balance } = data;
    const isDisValue: 1 | 0 = is_discount ? 1 : 0;
    const isCreditValue: 1 | 0 = type === "is_credit" ? 1 : 0;
    const payload = {
      name,
      balance,
      ...(mode === "edit" && isCashOnly
        ? {}
        : {
            is_cash: type === "is_cash",
            is_credit: isCreditValue,
            is_discount: isDisValue,
          }),
    };
    if (mode === "add") {
      mutate(payload);
    } else {
      mutateUpdate({
        id: isCashOnly ? paymentData.source_id : paymentData.id,
        ...payload,
      });
    }
  };

  useEffect(() => {
    if (paymentData) {
      reset({
        name: paymentData.name,
        balance: paymentData.balance ?? paymentData?.start_balance ?? 0, // start_balance for tree payments
        type: isCashOnly || paymentData.is_cash ? "is_cash" : "is_credit",
        is_discount: !!paymentData.is_discount,
      });
    }
  }, [paymentData]);

  //Add user success and error handling
  useEffect(() => {
    if (status === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update user success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? t("addNewPayment") : t("editPayment")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("paymentName")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                  disabled={paymentData?.hasTransactions}
                />
              )}
            />
            <Controller
              name="balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  label={t("startBalance")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.balance}
                  helperText={formState.errors.balance?.message}
                  disabled={
                    paymentData?.hasTransactions ||
                    paymentData?.type === "other_revenue"
                  }
                />
              )}
            />
            {!isCashOnly && (
              <Box>
                <Stack gap={2} alignItems={"flex-start"}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={{ xs: 1, sm: 1, md: 3 }}
                    flexWrap={"wrap"}
                    width={"100%"}
                  >
                    <Typography color={"textSecondary"}>{t("type")}</Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!formState?.errors.type}
                      sx={{
                        width: "fit-content",
                        ml: { xs: "auto", sm: "unset" },
                      }}
                    >
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                          >
                            <FormControlLabel
                              value="is_cash"
                              control={
                                <Radio
                                  icon={<CircleOutlinedIcon />}
                                  checkedIcon={<CheckCircleIcon />}
                                />
                              }
                              label={t("cash")}
                              sx={{ color: "#98A2B3" }}
                              disabled={paymentData?.type === "other_revenue"}
                            />
                            <FormControlLabel
                              value="is_credit"
                              control={
                                <Radio
                                  icon={<CircleOutlinedIcon />}
                                  checkedIcon={<CheckCircleIcon />}
                                />
                              }
                              label={t("creditAsPostponed")}
                              sx={{ color: "#98A2B3" }}
                              disabled={paymentData?.type === "other_revenue"}
                            />
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Stack>
                  <FormHelperText
                    error
                    id="my-helper-text"
                    sx={{ mt: -3, px: 2 }}
                  >
                    {formState?.errors.type?.message}
                  </FormHelperText>
                </Stack>

                <Controller
                  name="is_discount"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ mt: 1.5 }}
                      control={<Checkbox {...field} color="primary" />}
                      label={t("isDiscount")}
                      checked={field.value}
                      disabled={
                        paymentData?.type === "other_revenue" ||
                        paymentData?.is_aggregator
                      }
                    />
                  )}
                />
              </Box>
            )}
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              loading={isPending || isPendingUpdate}
              variant="contained"
              fullWidth
            >
              {mode === "add" ? generalT("add") : generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditPayment;
